import { Check, Link as LinkIcon, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { Integration } from "@/hooks/useIntegrations";
import { API_BASE } from "@/lib/api";
import { IntegrationIcon } from "./IntegrationIcon";

interface IntegrationCardProps {
  integration: Integration;
  onDisconnect: (provider: string) => void;
}

export function IntegrationCard({
  integration,
  onDisconnect,
}: IntegrationCardProps) {
  const handleConnect = () => {
    if (integration.connectUrl) {
      window.location.href = `${API_BASE}${integration.connectUrl}`;
    }
  };

  return (
    <Card className="flex flex-col justify-between">
      <CardHeader className="flex flex-row items-center gap-3 space-y-0">
        <IntegrationIcon id={integration.icon} />
        <div className="min-w-0 flex-1">
          <CardTitle className="text-base">{integration.name}</CardTitle>
          <Badge variant="secondary" className="mt-1 text-xs font-normal">
            {integration.category}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          {integration.description}
        </p>
      </CardContent>
      <CardFooter>
        {integration.connected ? (
          <div className="flex w-full items-center justify-between">
            <Button variant="outline" size="sm" disabled className="gap-1.5">
              <Check className="h-4 w-4 text-green-600" />
              Connected
            </Button>
            {integration.canDisconnect && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDisconnect(integration.id)}
                className="text-muted-foreground hover:text-destructive"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        ) : (
          <Button
            onClick={handleConnect}
            className="w-full gap-1.5"
            variant="outline"
          >
            <LinkIcon className="h-4 w-4" />
            Connect
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
